import { useState } from "react";
import type { Area, LatLon } from "@radar-sim/schema";
import { buildScenarioFromCourse } from "./buildScenarioFromCourse.js";
import {
  buildLibraryEntry,
  saveScenario,
} from "../authoring/library.js";

/**
 * In-app course recorder. The instructor enables record-mode, taps
 * the chart to drop course points one-by-one, and the recorder
 * shows a JSON snippet ready to paste into a scenario's
 * `behaviour: { name: "race-fleet", course: [...] }`.
 *
 * Decoupled from ChartView via a shared "recording" state lifted to
 * SessionRunner — the chart's click handler routes taps here when
 * recording is active, in the same pattern as click-to-place spawn.
 */
export interface CourseRecorderApi {
  recording: boolean;
  points: ReadonlyArray<LatLon>;
  start: () => void;
  stop: () => void;
  add: (p: LatLon) => void;
  removeLast: () => void;
  clear: () => void;
}

export function useCourseRecorder(): CourseRecorderApi {
  const [recording, setRecording] = useState(false);
  const [points, setPoints] = useState<LatLon[]>([]);
  return {
    recording,
    points,
    start: () => {
      setRecording(true);
      setPoints([]);
    },
    stop: () => setRecording(false),
    add: (p: LatLon) => setPoints((prev) => [...prev, p]),
    removeLast: () => setPoints((prev) => prev.slice(0, -1)),
    clear: () => setPoints([]),
  };
}

export function CourseRecorderPanel(props: {
  api: CourseRecorderApi;
  area: Area;
  onScenarioSaved?: (scenarioId: string) => void;
}): JSX.Element | null {
  const { api, area } = props;
  const [copied, setCopied] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [fleetSize, setFleetSize] = useState(8);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAs, setSavedAs] = useState<string | null>(null);
  if (!api.recording && api.points.length === 0) {
    return (
      <button
        onClick={api.start}
        style={{
          background: "var(--bg-card)",
          color: "var(--text)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-sm)",
          padding: "8px 10px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
        title="Tap chart to drop course points; emits a race-fleet course snippet"
      >
        ⊕ Record course
      </button>
    );
  }
  const json = JSON.stringify(api.points, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const doSave = () => {
    setSaveError(null);
    const result = buildScenarioFromCourse({
      name: saveName.trim().length > 0 ? saveName.trim() : "Recorded course",
      area,
      course: api.points,
      fleetSize,
    });
    if (!result.ok) {
      setSaveError(result.error);
      return;
    }
    saveScenario(buildLibraryEntry(result.json, result.scenario));
    setSavedAs(result.scenario.metadata.id);
    setSaveOpen(false);
    props.onScenarioSaved?.(result.scenario.metadata.id);
  };
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${api.recording ? "var(--accent)" : "var(--border)"}`,
        borderRadius: "var(--radius-md)",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 12,
        }}
      >
        <strong style={{ color: api.recording ? "var(--accent)" : "var(--text)" }}>
          {api.recording ? "Recording course" : "Course saved"}
        </strong>
        <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
          {api.points.length} points
        </span>
      </div>
      {api.points.length > 0 && (
        <pre
          style={{
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "6px 8px",
            margin: 0,
            fontSize: 10,
            fontFamily: "var(--font-mono)",
            color: "var(--text-muted)",
            maxHeight: 120,
            overflow: "auto",
          }}
        >
          {json}
        </pre>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {api.recording ? (
          <button
            onClick={api.stop}
            style={btnStyle("var(--accent)", "var(--accent-fg)")}
          >
            Done
          </button>
        ) : (
          <button onClick={api.start} style={btnStyle()}>
            Resume
          </button>
        )}
        <button
          onClick={api.removeLast}
          disabled={api.points.length === 0}
          style={btnStyle()}
        >
          Undo
        </button>
        <button onClick={copy} style={btnStyle()} disabled={api.points.length === 0}>
          {copied ? "Copied ✓" : "Copy JSON"}
        </button>
        <button
          onClick={() => setSaveOpen((v) => !v)}
          style={btnStyle(
            saveOpen ? "var(--accent)" : "transparent",
            saveOpen ? "var(--accent-fg)" : "var(--text)",
          )}
          disabled={api.points.length < 2}
          title={
            api.points.length < 2
              ? "Need at least two course points"
              : "Save as a re-runnable scenario"
          }
        >
          ⤓ Save as scenario
        </button>
        <button onClick={api.clear} style={btnStyle()}>
          Clear
        </button>
      </div>
      {saveOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 8,
          }}
        >
          <label style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Name
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. North Channel laps"
              style={{
                width: "100%",
                marginTop: 2,
                padding: "5px 7px",
                fontSize: 12,
                background: "var(--bg-card)",
                color: "var(--text)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--radius-sm)",
              }}
            />
          </label>
          <label style={{ fontSize: 11, color: "var(--text-muted)" }}>
            Fleet size: {fleetSize}
            <input
              type="range"
              min={1}
              max={20}
              value={fleetSize}
              onChange={(e) => setFleetSize(parseInt(e.target.value, 10))}
              style={{ width: "100%", accentColor: "var(--accent)" }}
            />
          </label>
          {saveError !== null && (
            <span style={{ color: "var(--danger)", fontSize: 11 }}>
              {saveError}
            </span>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={doSave}
              style={btnStyle("var(--accent)", "var(--accent-fg)")}
            >
              Save
            </button>
            <button onClick={() => setSaveOpen(false)} style={btnStyle()}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {savedAs !== null && !saveOpen && (
        <span
          style={{
            fontSize: 11,
            color: "var(--accent)",
            fontFamily: "var(--font-mono)",
          }}
        >
          Saved as {savedAs} — pick from the scenario menu to launch.
        </span>
      )}
    </div>
  );
}

function btnStyle(bg = "transparent", color = "var(--text)"): React.CSSProperties {
  return {
    background: bg,
    color,
    border: "1px solid var(--border-strong)",
    borderRadius: "var(--radius-sm)",
    padding: "5px 9px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
  };
}
